import { useEffect, useRef } from 'react';
import {
  PdfHighlighter,
  type PdfHighlighterUtils,
  PdfLoader,
} from 'react-pdf-highlighter-extended';
import HighlightContainer from './HighlightContainer';
import type { CommentedHighlight } from './types';

// Bundle the worker here - it must match our pdfjs-dist version exactly
// the default behaviour of react-pdf-highlighter is to use unpkg
// with hardcoded version in the URL.
import pdfJSWorkerURL from 'pdfjs-dist/build/pdf.worker?url';

export const PDFContainer = ({
  url,
  highlights,
  activeHighlight,
  onScrollAway,
  selectionTip,
  enableAreaSelection,
  onLoad,
  overrideActiveHighlight,
}: {
  url: string;
  highlights: Array<CommentedHighlight>;
  activeHighlight: string | null;
  enableAreaSelection: (event: MouseEvent) => boolean;
  onScrollAway?(): void;
  selectionTip?: React.ReactNode;
  onLoad?: () => void;
  overrideActiveHighlight?: CommentedHighlight;
}) => {
  // Refs for PdfHighlighter utilities
  const highlighterUtilsRef = useRef<PdfHighlighterUtils>();

  useEffect(() => {
    if (overrideActiveHighlight) {
      highlighterUtilsRef.current?.scrollToHighlight(overrideActiveHighlight);
    }
  }, [overrideActiveHighlight]);

  // TODO: Scroll on load
  //       see https://github.com/DanielArnould/react-pdf-highlighter-extended/issues/7#issuecomment-2074238557
  //       for potential workaround
  useEffect(() => {
    if (!activeHighlight) {
      return;
    }
    const highlight = highlights.find(
      (highlight) => highlight.id === activeHighlight,
    );
    if (!highlight) {
      return;
    }
    if (!highlighterUtilsRef.current) {
      return;
    }
    const viewer = highlighterUtilsRef.current.getViewer();
    if (!viewer) {
      console.error('Highlight scroll error, out of bounds?', {
        highlight,
      });
      return;
    }
    const pageView = viewer.getPageView(
      highlight.position.boundingRect.pageNumber,
    );
    if (pageView === undefined) {
      console.error('Highlight scroll error, out of bounds?', {
        highlight,
      });
      return;
    }

    highlighterUtilsRef.current.scrollToHighlight(highlight);
    // TODO: Not sure we even want highlights in dependencies here
  }, [activeHighlight, highlights]);

  return (
    <div className="App" style={{ display: 'flex', height: '100%' }}>
      <div
        style={{
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          flexGrow: 1,
        }}
      >
        <PdfLoader
          errorMessage={() => (
            <div className="flex align-items-center	 justify-content-center	">
              <div className="flex flex-col align-items-center justify-content-center p-6 mt-6 bg-gray-50 border border-gray-300 rounded-lg shadow-md">
                <i
                  className="pi pi-exclamation-circle text-red-500 text-4xl"
                  aria-hidden="true"
                ></i>
                <p className="text-gray-700 text-center text-sm pl-3">
                  Something went wrong while fetching the PDF file
                </p>
              </div>
            </div>
          )}
          document={url}
          workerSrc={pdfJSWorkerURL}
        >
          {(pdfDocument) => (
            <PdfHighlighter
              // Maybe only when in edit mode?
              enableAreaSelection={enableAreaSelection}
              selectionTip={selectionTip}
              pdfDocument={pdfDocument}
              pdfViewerOptions={{
                // Avoid displaying pdf forms as editable
                // by default they render as interactive <input />
                // elements which is just confusing.
                annotationMode: 1,
              }}
              onScrollAway={onScrollAway}
              utilsRef={(_pdfHighlighterUtils) => {
                highlighterUtilsRef.current = _pdfHighlighterUtils;
              }}
              highlights={highlights}
              style={{
                height: '100%',
              }}
            >
              {(() => {
                pdfDocument && onLoad && onLoad();
                return true;
              }) && <HighlightContainer />}
            </PdfHighlighter>
          )}
        </PdfLoader>
      </div>
    </div>
  );
};
